import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getFavoriteIds, toggleFavorite } from "@/api/favorites";
import { trackEvent } from "@/lib/analytics";
import { useAuthStore } from "@/store/authStore";

const FAVORITE_IDS_KEY = ["favoriteIds"] as const;

/** 즐겨찾기 word_id 목록 조회 */
export function useFavoriteIdsQuery() {
  const userId = useAuthStore(state => state.user?.id);

  return useQuery({
    queryKey: [...FAVORITE_IDS_KEY, userId],
    queryFn: () => getFavoriteIds(userId!),
    enabled: Boolean(userId),
  });
}

/** 즐겨찾기 토글 (낙관적 업데이트) */
export function useToggleFavoriteMutation() {
  const queryClient = useQueryClient();
  const userId = useAuthStore(state => state.user?.id);

  return useMutation({
    mutationFn: (wordId: string) => toggleFavorite(userId!, wordId),
    onMutate: async (wordId: string) => {
      const queryKey = [...FAVORITE_IDS_KEY, userId];
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<string[]>(queryKey) ?? [];
      const isFavorited = previous.includes(wordId);
      const next = isFavorited ? previous.filter(id => id !== wordId) : [...previous, wordId];
      queryClient.setQueryData(queryKey, next);

      trackEvent("favorite_toggle", {
        word_id: wordId,
        action: isFavorited ? "unfavorite" : "favorite",
      });

      return { previous };
    },
    onError: (_err, _wordId, context) => {
      if (context?.previous) {
        queryClient.setQueryData([...FAVORITE_IDS_KEY, userId], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [...FAVORITE_IDS_KEY, userId] });
    },
  });
}
